import { Helmet } from "react-helmet";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import worksheets from "../data/worksheets.json";

const classSlugToLabel = {
  "lkg": "LKG",
  "ukg": "UKG",
  "class-1": "I Class",
  "class-2": "II Class",
  "class-3": "III Class",
  "class-4": "IV Class",
  "class-5": "V Class",
  "class-6": "VI Class",
  "class-7": "VII Class"
};

const subjectSlugToLabel = {
  "maths": "Maths",
  "english": "English",
  "science": "Science",
  "tracing": "Tracing",
  "coloring": "Coloring",
  "matching": "Matching",
  "puzzle": "Puzzle"
};

const gradeToClass = (grade) => {
  if (grade === "LKG") return "LKG";
  if (grade === "UKG") return "UKG";
  if (grade === "1") return "I Class";
  if (grade === "2") return "II Class";
  if (grade === "3") return "III Class";
  if (grade === "4") return "IV Class";
  if (grade === "5") return "V Class";
  if (grade === "6") return "VI Class";
  if (grade === "7") return "VII Class";
  return grade;
};

const subjectToLabel = (subject) => {
  if (!subject) return "";
  const s = subject.toLowerCase();
  if (s === "math" || s === "maths") return "Maths";
  if (s === "science") return "Science";
  if (s === "english") return "English";
  if (s === "tracing") return "Tracing";
  if (s === "coloring") return "Coloring";
  if (s === "matching") return "Matching";
  if (s === "puzzle") return "Puzzle";
  return subject;
};

const lkgEnglishChapters = [
  { title: "Pre-Writing Strokes (Foundational)", slug: "pre-writing-strokes-foundational" },
  { title: "Capital Letters (A-Z)", slug: "capital-letters-a-z" },
  { title: "Small Letters (a-z)", slug: "small-letters-a-z" },
  { title: "Basic Phonetics & Vocabulary", slug: "basic-phonetics-vocabulary" },
  { title: "Basic Grammar & Vocabulary", slug: "basic-grammar-vocabulary" },
  { title: "Picture Reading & Conversation", slug: "picture-reading-conversation" }
];

const lkgEnglishChapterPrefixes = {
  "pre-writing-strokes-foundational": "pre-writing strokes",
  "capital-letters-a-z": "capital letters",
  "small-letters-a-z": "small letters",
  "basic-phonetics-vocabulary": "basic phonetics",
  "basic-grammar-vocabulary": "basic grammar",
  "picture-reading-conversation": "picture reading"
};

const lkgMathsChapters = [
  { title: "Number Sense (0 to 50)", slug: "number-sense-0-to-50", prefix: "number sense" },
  { title: "Counting and Representation", slug: "counting-and-representation", prefix: "counting and representation" },
  { title: "Basic Shapes", slug: "basic-shapes", prefix: "basic shapes" },
  { title: "Classifying and Sorting", slug: "classifying-and-sorting", prefix: "classifying and sorting" },
  { title: "Comparisons", slug: "comparisons", prefix: "comparisons" },
  { title: "Spatial Awareness", slug: "spatial-awareness", prefix: "spatial awareness" },
  { title: "Patterns", slug: "patterns", prefix: "patterns" }
];

const withCacheBust = (src) => {
  if (!src) return src;
  return src.includes("?") ? `${src}&v=2` : `${src}?v=2`;
};

function WorksheetsSeoPage(){
  const navigate = useNavigate();
  const { classSlug, subjectSlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const chapter = searchParams.get("chapter");
  const toggleChapter = (slug) => {
    const next = new URLSearchParams(searchParams);
    if (chapter === slug) {
      next.delete("chapter");
    } else {
      next.set("chapter", slug);
    }
    setSearchParams(next, { replace: true });
  };

  const classLabel = classSlug ? classSlugToLabel[classSlug] : null;
  const subjectLabel = subjectSlug ? subjectSlugToLabel[subjectSlug] : null;

  const filtered = worksheets.filter((w) => {
    const c = gradeToClass(String(w.grade));
    const s = subjectToLabel(String(w.subject));
    const classOk = classLabel ? c === classLabel : true;
    const subjectOk = subjectLabel ? s === subjectLabel : true;
    return classOk && subjectOk;
  });

  const pageTitleParts = [];
  if (classLabel) pageTitleParts.push(classLabel);
  if (subjectLabel) pageTitleParts.push(subjectLabel);
  const title = pageTitleParts.length
    ? `${pageTitleParts.join(" ")} Worksheets – PracticeNest`
    : "Worksheets – PracticeNest";

  const description = pageTitleParts.length
    ? `Free printable ${pageTitleParts.join(" ")} worksheets and practice sheets for kids.`
    : "Free printable worksheets and practice sheets for kids.";

  const canonical = classSlug && subjectSlug
    ? `https://practicenest.in/worksheets/${classSlug}/${subjectSlug}`
    : classSlug
      ? `https://practicenest.in/worksheets/${classSlug}`
      : subjectSlug
        ? `https://practicenest.in/worksheets/subject/${subjectSlug}`
        : "https://practicenest.in/worksheets";

  return (
    <div style={{ minHeight: "100vh", padding: "24px", background: "#F9FAFF" }}>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
      </Helmet>

      <div style={{ maxWidth: "980px", margin: "0 auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "18px" }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              padding: "6px 10px",
              borderRadius: "10px",
              border: "1px solid #cfd8e3",
              background: "#fff",
              cursor: "pointer",
              fontWeight: "700"
            }}
          >
            ← Back
          </button>
          <Link to="/worksheets" style={{ textDecoration: "none", fontWeight: "700" }}>All Worksheets</Link>
        </div>

        <h1 style={{ fontSize: "32px", marginBottom: "6px" }}>
          {classLabel ? `${classLabel} ` : ""}{subjectLabel ? `${subjectLabel} ` : ""}Worksheets
        </h1>
        <p style={{ marginTop: 0, marginBottom: "18px" }}>
          Free printable worksheets for {classLabel ? classLabel : "all classes"}
          {subjectLabel ? ` in ${subjectLabel}` : ""}.
        </p>

        {classSlug === "lkg" && subjectSlug === "english" ? (
          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "16px" }}>
            {lkgEnglishChapters.map((c, idx) => (
              <div key={c.slug} style={{ display: "grid", gap: "12px" }}>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleChapter(c.slug)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") toggleChapter(c.slug);
                  }}
                  style={{
                    textDecoration: "none",
                    color: "inherit",
                    background: "#fff",
                    padding: "20px",
                    borderRadius: "16px",
                    border: "1px solid #e6eef7",
                    boxShadow: "0 6px 14px rgba(0,0,0,0.05)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer"
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "18px" }}>
                    <div style={{ fontSize: "22px", fontWeight: "700", color: "#5B6B7B", width: "36px" }}>
                      {String(idx + 1).padStart(2, "0")}
                    </div>
                    <div>
                      <div style={{ fontSize: "20px", fontWeight: "700" }}>{c.title}</div>
                      <div style={{ fontSize: "14px", color: "#7A8896" }}>Chapter</div>
                    </div>
                  </div>
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleChapter(c.slug);
                    }}
                    style={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "8px",
                    border: "1px solid #cfd8e3",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#9AA7B2",
                    fontSize: "16px"
                  }}>
                    {chapter === c.slug ? "▴" : "▾"}
                  </div>
                </div>

                {chapter === c.slug && lkgEnglishChapterPrefixes[c.slug] && (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "16px" }}>
                    {filtered
                      .filter((w) =>
                        String(w.title).toLowerCase().startsWith(lkgEnglishChapterPrefixes[c.slug])
                      )
                      .map((w) => (
                        <div key={w.id} style={{ background: "#fff", padding: "16px", borderRadius: "14px", boxShadow: "0 8px 18px rgba(0,0,0,0.06)" }}>
                          <div style={{ fontSize: "12px", color: "#666", marginBottom: "6px" }}>
                            {gradeToClass(String(w.grade))} • {subjectToLabel(String(w.subject))}
                          </div>
                          <h3 style={{ marginTop: 0 }}>{w.title}</h3>
                          {w.preview && (
                            <img src={withCacheBust(w.preview)} alt="preview" style={{ width: "100%", borderRadius: "8px", marginBottom: "10px" }} />
                          )}
                          <a
                            href={withCacheBust(w.file)}
                            download
                            style={{
                              display: "inline-block",
                              padding: "10px 14px",
                              fontSize: "14px",
                              fontWeight: "700",
                              background: "#2D7FF9",
                              color: "#fff",
                              borderRadius: "10px",
                              textDecoration: "none"
                            }}
                          >
                            Download
                          </a>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : classSlug === "lkg" && subjectSlug === "maths" ? (
          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "16px" }}>
            {lkgMathsChapters.map((c, idx) => (
              <div key={c.slug}>
                <div
                  onClick={() => toggleChapter(c.slug)}
                  style={{
                    textDecoration: "none",
                    color: "inherit",
                    background: "#fff",
                    padding: "20px",
                    borderRadius: "16px",
                    border: "1px solid #e6eef7",
                    boxShadow: "0 6px 14px rgba(0,0,0,0.05)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer"
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "18px" }}>
                    <div style={{ fontSize: "22px", fontWeight: "700", color: "#5B6B7B", width: "36px" }}>
                      {String(idx + 1).padStart(2, "0")}
                    </div>
                    <div>
                      <div style={{ fontSize: "20px", fontWeight: "700" }}>{c.title}</div>
                      <div style={{ fontSize: "14px", color: "#7A8896" }}>Chapter</div>
                    </div>
                  </div>
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleChapter(c.slug);
                    }}
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "8px",
                      border: "1px solid #cfd8e3",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#9AA7B2",
                      fontSize: "16px",
                      cursor: "pointer"
                    }}
                  >
                    {chapter === c.slug ? "▴" : "▾"}
                  </div>
                </div>

                {chapter === c.slug && c.prefix && (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "16px", marginTop: "12px" }}>
                    {filtered
                      .filter((w) =>
                        String(w.title).toLowerCase().startsWith(c.prefix)
                      )
                      .map((w) => (
                        <div key={w.id} style={{ background: "#fff", padding: "16px", borderRadius: "14px", boxShadow: "0 8px 18px rgba(0,0,0,0.06)" }}>
                          <div style={{ fontSize: "12px", color: "#666", marginBottom: "6px" }}>
                            {gradeToClass(String(w.grade))} • {subjectToLabel(String(w.subject))}
                          </div>
                          <h3 style={{ marginTop: 0 }}>{w.title}</h3>
                          {w.preview && (
                            <img src={withCacheBust(w.preview)} alt="preview" style={{ width: "100%", borderRadius: "8px", marginBottom: "10px" }} />
                          )}
                          <a
                            href={withCacheBust(w.file)}
                            download
                            style={{
                              display: "inline-block",
                              padding: "10px 14px",
                              fontSize: "14px",
                              fontWeight: "700",
                              background: "#2D7FF9",
                              color: "#fff",
                              borderRadius: "10px",
                              textDecoration: "none"
                            }}
                          >
                            Download
                          </a>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ background: "#fff", padding: "16px", borderRadius: "12px", border: "1px solid #eee" }}>
            More worksheets are coming soon.
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "16px" }}>
            {filtered.map((w) => (
              <div key={w.id} style={{ background: "#fff", padding: "16px", borderRadius: "14px", boxShadow: "0 8px 18px rgba(0,0,0,0.06)" }}>
                <div style={{ fontSize: "12px", color: "#666", marginBottom: "6px" }}>
                  {gradeToClass(String(w.grade))} • {subjectToLabel(String(w.subject))}
                </div>
                <h3 style={{ marginTop: 0 }}>{w.title}</h3>
                {w.preview && (
                  <img src={withCacheBust(w.preview)} alt="preview" style={{ width: "100%", borderRadius: "8px", marginBottom: "10px" }} />
                )}
                <a
                  href={withCacheBust(w.file)}
                  download
                  style={{
                    display: "inline-block",
                    padding: "10px 14px",
                    fontSize: "14px",
                    fontWeight: "700",
                    background: "#2D7FF9",
                    color: "#fff",
                    borderRadius: "10px",
                    textDecoration: "none"
                  }}
                >
                  Download
                </a>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default WorksheetsSeoPage;
